import { Box, Button, FormField, Select, Image, Stack, Text, TextInput, Avatar, CardBody, CardFooter, CardHeader, Grid, Meter } from "grommet";
import React, { useEffect } from "react";
import ThemeViewOld from "./ThemeViewOld";
import axios from "./general/HttpClient";
import { BrowseThemes, CreateTheme, GenerateThemeBorder, GenerateThemeImage, GenerateThemes, GetTheme, Theme } from "./ThemeStore";
import { set, take } from "lodash";
import { Faction } from "./FactionsHome";
import ParallaxTilt from "./components/ParallaxTilt2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GenerateFactionImage } from "./FactionsStore";
import { GenerateCampaignImage } from "./CampaignsStore";
import CampaignView from "./campaigns/CampaignView";
import FactionView from "./factions/FactionView";
import ThemeView from "./themes/ThemeView";
import CardView, { BlankCard } from "./CardView";
import { Card } from "./Home";
import { GenerateCardImage } from "./CardStore";
import CraftCard from "./CraftCard";
import { SparkLineProgress } from "./general/Loaders";

export interface Campaign {
    id: number;
    title: string;
    conflict: string;
    visualDescription: string;
    version: string;
    themeImageID: number | null;
    factionSetID: string;
}

export interface PreconstructedDeck {
    id: number;
    name: string;
    description: string;
    factionID: number;
    faction: Faction;
    campaignID: number;
    campaign: Campaign;
    energyReserve: number;
    currencyReserve: number;
    knowledgeReserve: number;
    artifactReserve: number;
    cards: PreconstructedDeckCard[];
}

export interface PreconstructedDeckCard {
    id: number;
    cardID: number;
    card: Card;
    preconstructedDeckID: number;
    preconstructedDeck: PreconstructedDeck;
}
export interface CardCost {
    resourceAmounts: ResourceAmount[];
}

export interface ResourceAmount {
    amount: number;
    resource: string;
}

const Artefaxist = () => {
    const [step, setStep] = React.useState("start" as "start" | "theme" | "faction" | "precon");

    const [loading, setLoading] = React.useState(false);
    const [message, setMessage] = React.useState("");

    const [settingPrompt, setSettingPrompt] = React.useState("");

    const [themes, setThemes] = React.useState([] as Theme[]);
    const [selectedTheme, setSelectedTheme] = React.useState({} as Theme);

    const [factions, setFactions] = React.useState([] as Faction[]);
    const [selectedFaction, setSelectedFaction] = React.useState({} as Faction);

    const [campaigns, setCampaigns] = React.useState([] as Campaign[]);
    const [selectedCampaign, setSelectedCampaign] = React.useState({} as Campaign);

    const listThemes = () => {
        axios.get("/api/themes/browse").then((response) => {
            setThemes(response.data);
        });
    };

    const selectTheme = (theme) => {
        setSelectedTheme(theme);
    };

    React.useEffect(() => {
        listThemes();
    }, []);

    // const generateThemes = async () => {
    //     setLoading(true);
    //     if (selectedTheme.id) {
    //         chooseTheme(selectedTheme);
    //     } else {
    //         // setMessage("Generating Themes...");
    //         setStep("theme");
    //         // GenerateThemes(settingPrompt).then((response) => {
    //         //     setThemes(response.data);
    //         //     // listThemes();
    //         //     setLoading(false);
    //         // });
    //     }
    // };

    const createOrChooseTheme = async () => {
        if (selectedTheme.id) {
            chooseTheme(selectedTheme);
        } else {
            setLoading(true);
            setMessage("Creating Theme...");
            let response = await CreateTheme(settingPrompt);
            setSelectedTheme(response.data);
            chooseTheme(response.data);
        }
    };

    const [showThemeImage, setShowThemeImage] = React.useState(false);
    const [showThemeBorder, setShowThemeBorder] = React.useState(false);

    const chooseTheme = async (theme: Theme) => {
        setSelectedFaction({} as Faction);
        setSelectedCampaign({} as Campaign);
        setLoading(true);

        if (!theme.themeImageID) {
            setMessage("Generating Theme Image...");
            let themeResponse = await GenerateThemeImage(theme.id, setMessage);
        }
        setShowThemeImage(true);

        if (!theme.borderImageID) {
            setMessage("Generating Theme Border...");
            let themeResponse = await GenerateThemeBorder(theme.id, setMessage);
            let updatedTheme = await GetTheme(theme.id);
            setSelectedTheme(updatedTheme.data);
        }
        setShowThemeBorder(true);
        setStep("theme");
        setLoading(false);
    };

    const confirmTheme = async () => {
        let response = await axios.get("api/factions/GetFactionsForTheme", { params: { themeID: selectedTheme.id } });
        if (response.data.length == 0) {
            await generateFactions(selectedTheme);
            setLoading(false);
        } else {
            setFactions(response.data);
            setLoading(false);
        }
        setStep("faction");
    };

    const generateFactions = async (theme) => {
        setLoading(true);
        setMessage("Generating Factions...");
        return await axios.get("/api/factions/GenerateFactions", { params: { themeID: theme.id } }).then((response) => {
            setFactions(response.data);
        });
    };

    React.useEffect(() => {
        if (factions.length > 0) {
            setupFactions();
        }
    }, [factions]);

    const [showFactionImages, setShowFactionImages] = React.useState(false);

    const setupFactions = async () => {
        setLoading(true);
        let reloadFactions = false;
        for (let faction of factions) {
            if (!faction.imageID) {
                let response = await GenerateFactionImage(faction.id, setMessage);
                reloadFactions = true;
            }
        }
        if (reloadFactions) {
            let response = await axios.get("api/factions/GetFactionsForTheme", { params: { themeID: selectedTheme.id } });
            setFactions(response.data);
        }
        setShowFactionImages(true);
        setLoading(false);
    };

    const chooseFaction = async () => {
        setSelectedCampaign({} as Campaign);
        setLoading(true);

        setStep("precon");
        let preconDecks = await getPreconDecksForFaction();

        if (preconDecks.length == 1) {
            setPreconDeck(preconDecks[0]);
        } else {
            await createPreconDeck();
        }

        setLoading(false);
    };

    const [preconDeck, setPreconDeck] = React.useState({} as PreconstructedDeck);

    const getPreconDecksForFaction = () => {
        return axios.get("/api/decks/preconByFaction", { params: { faction: selectedFaction.id } }).then((response) => {
            return response.data;
        });
    };

    const createPreconDeck = () => {
        return axios.post("/api/decks/precon", { name: selectedFaction.name, factionID: selectedFaction.id, campaignID: selectedCampaign.id }).then((response) => {
            setPreconDeck(response.data);
        });
    };

    const [refunding, setRefunding] = React.useState(false);

    const refundCard = async (card) => {
        setRefunding(true);

        let response = await axios.post(`/api/decks/precon/${preconDeck.id}/cards$refund`, card);

        let deckResponse = await axios.get(`/api/decks/precon/${preconDeck.id}`);

        setPreconDeck(deckResponse.data);

        setRefunding(false);
    };

    const onCraftDone = async () => {
        let deckResponse = await axios.get(`/api/decks/precon/${preconDeck.id}`);

        setPreconDeck(deckResponse.data);
    };

    const [crafting, setCrafting] = React.useState(false);
    const [craftMessage, setCraftMessage] = React.useState("");

    const craftPreconCardPack = async (deckID) => {
        setCrafting(true);
        let timer = setTimeout(() => {
            setCraftMessage("Taking longer than usual, must be something interesting coming");
        }, 60000);
        let response = await axios.post(`/api/decks/precon/${deckID}/cards$craftPack`);
        clearTimeout(timer);
        onCraftDone();
        setCrafting(false);
        setCraftMessage("");
    };

    const craftPreconCardCommander = async (deckID) => {
        setCrafting(true);
        let timer = setTimeout(() => {
            setCraftMessage("Taking longer than usual, must be something interesting coming");
        }, 60000);
        let response = await axios.post(`/api/decks/precon/${deckID}/cards$craftCommander`);
        clearTimeout(timer);
        onCraftDone();
        setCrafting(false);
        setCraftMessage("");
    };

    return (
        <>
            <Box align="center">
                <Stack anchor="bottom-right">
                    <Image src="/images/logo-small.png" width={"250px"}></Image>
                    <Image src="/images/beta.png" width={"75px"} style={{ margin: "25px" }}></Image>
                </Stack>
                <Text size="large">Artefaxist</Text>
                {loading && <Box pad="small">{message}</Box>}

                {step == "start" && (
                    <>
                        <Box width={"medium"} gap="small">
                            <TextInput textAlign="center" placeholder="What is the setting of your game" value={settingPrompt} onChange={(e) => setSettingPrompt(e.target.value)} />
                            <Text>OR</Text>
                            <Select value={selectedTheme} options={themes} labelKey={"name"} onChange={({ option }) => selectTheme(option)}></Select>
                            {<Button label="Continue" onClick={createOrChooseTheme} disabled={loading || (settingPrompt.length < 8 && !selectedTheme.id)}></Button>}
                        </Box>
                    </>
                )}

                {step == "theme" && (
                    <>
                        <Box pad="small" gap="small">
                            <Box style={{ zoom: "0.7" }}>
                                <BlankCard borderColor={"black"} showBorder={showThemeBorder} showImage={showThemeImage} borderImageID={selectedTheme.borderImageID} imageID={selectedTheme.themeImageID}>
                                    <></>
                                </BlankCard>
                            </Box>
                            <Button onClick={confirmTheme} disabled={loading} label={"Confirm"}></Button>
                        </Box>
                    </>
                )}
                {step == "faction" && (
                    <>
                        <Box direction="row" pad="small" gap="small">
                            <Box style={{ zoom: "0.7" }}>
                                <BlankCard borderColor={"black"} showBorder={showThemeBorder} showImage={showThemeImage} borderImageID={selectedTheme.borderImageID} imageID={selectedTheme.themeImageID}>
                                    <></>
                                </BlankCard>
                            </Box>
                            <Box>
                                {!loading && <Text size="medium">Your Factions, choose one to play</Text>}
                                <Box pad="small" gap="small" wrap justify="center">
                                    {factions.map((faction) => (
                                        <FactionView
                                            key={faction.id}
                                            faction={faction}
                                            onClick={() => {
                                                setSelectedFaction(faction);
                                            }}
                                            active={faction.id == selectedFaction.id}
                                            width="550px"
                                            height="200px"
                                            showText={faction.id == selectedFaction.id}
                                            showImage={showFactionImages}
                                        ></FactionView>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                        <Box width={"medium"}>
                            <Button label="Confirm Choice" disabled={!selectedFaction.id} onClick={chooseFaction}></Button>
                        </Box>
                    </>
                )}

                {/* {step == 3 && (
                    <>
                        {loading && <Box pad="small">{message}</Box>}

                        {!loading && (
                            <Grid columns={["290px", "660px"]} alignSelf="center" gap={"small"}>
                                <Box align="start">
                                    <ThemeView key={selectedTheme.id} theme={selectedTheme} onClick={() => chooseTheme(selectedTheme)} active={true} width="290px" height="544px"></ThemeView>
                                </Box>
                                <Box>
                                    <FactionView key={selectedTheme.id} faction={selectedFaction} onClick={() => {}} active={true} width="660px" height="264px" showText={false}></FactionView>
                                </Box>
                            </Grid>
                        )}

                        {!loading && <Text size="medium">Choose a Campaign</Text>}
                        <Box direction="row" pad="small" gap="small" wrap justify="center">
                            {campaigns.map((campaign) => (
                                <CampaignView key={campaign.id} campaign={campaign} onClick={() => setSelectedCampaign(campaign)} active={campaign.id == selectedCampaign.id} width="660px" height="264px" showText={true}></CampaignView>
                            ))}
                        </Box>
                        <Box width={"medium"}>
                            <Button label="Confirm Choice" disabled={!selectedCampaign.id} onClick={() => chooseCampaign()}></Button>
                        </Box>
                    </>
                )} */}

                {step == "precon" && (
                    <>
                        {!loading && (
                            <>
                                <Grid columns={["290px", "660px"]} alignSelf="center" gap={"small"}>
                                    <Box align="start" style={{ zoom: "0.5" }}>
                                        <BlankCard borderColor={"black"} showBorder={showThemeBorder} showImage={showThemeImage} borderImageID={selectedTheme.borderImageID} imageID={selectedTheme.themeImageID}>
                                            <></>
                                        </BlankCard>
                                    </Box>
                                    <Box>
                                        <FactionView faction={selectedFaction} onClick={() => {}} active={true} width="660px" height="264px" showText={false} showImage={showFactionImages}></FactionView>
                                    </Box>
                                </Grid>
                                <Box align="center">
                                    <Text>Precon - {preconDeck.name}</Text>
                                    <Grid columns={{ count: 2, size: "auto" }}>
                                        {/* <Box align="center">
                                            <div style={{ backgroundColor: "#0074D9", width: "30px", height: "30px", borderRadius: "100%" }}></div>
                                            <Text>Knowledge</Text>
                                            <Text>{preconDeck.knowledgeReserve}</Text>
                                        </Box>
                                        <Box align="center">
                                            <div style={{ backgroundColor: "#FF851B", width: "30px", height: "30px", borderRadius: "100%" }}></div>
                                            <Text>Artifact</Text>
                                            <Text>{preconDeck.artifactReserve}</Text>
                                        </Box> */}
                                        <Box align="center">
                                            <div style={{ backgroundColor: "#2ECC40", width: "30px", height: "30px", borderRadius: "100%" }}></div>
                                            <Text>Currency</Text>
                                            <Text>{preconDeck.currencyReserve}</Text>
                                        </Box>
                                        <Box align="center">
                                            <Button
                                                label="Buy 5 Cards (Costs 10 currency)"
                                                onClick={() => {
                                                    craftPreconCardPack(preconDeck.id);
                                                }}
                                            ></Button>
                                            <Button
                                                label="Recruit a commander"
                                                onClick={() => {
                                                    craftPreconCardCommander(preconDeck.id);
                                                }}
                                            ></Button>
                                        </Box>
                                        {/* <Box align="center">
                                            <div style={{ backgroundColor: "#FF4136", width: "30px", height: "30px", borderRadius: "100%" }}></div>
                                            <Text>Energy</Text>
                                            <Text>{preconDeck.energyReserve}</Text>
                                        </Box> */}
                                    </Grid>

                                    {crafting && <SparkLineProgress time="90" />}
                                    {craftMessage && <Text textAlign="center">{craftMessage}</Text>}

                                    <Box direction="row" wrap gap="small" pad="small" justify="center" style={{ zoom: 0.6 }}>
                                        {preconDeck.cards?.map((preconCard) => (
                                            <CardView key={preconCard.card.id} card={preconCard.card} onUpdate={() => {}}>
                                                {refunding && <Text>Refunding...</Text>}
                                                {!refunding && <Button size="small" onClick={() => refundCard(preconCard.card)} label="Refund"></Button>}
                                            </CardView>
                                        ))}
                                    </Box>

                                    {/* <CraftCard onDone={onCraftDone} deckID={preconDeck.id}></CraftCard> */}
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Box>
        </>
    );
};

export default Artefaxist;
